/**
 * IntegrationDashboardPage — `/integration`.
 *
 * Shows live KPI tiles from GET /v1/integration/dashboard on service-audit,
 * and the partner registry from GET /v1/integration/partners.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Key, Timer, Users, Webhook } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface DashboardKpis {
  activePartners: number;
  activeWebhooks: number;
  webhooksDeliveredToday: number;
  nibssStatus: string;
}

interface Partner {
  id: string;
  name: string;
  contactEmail: string;
  partnerType: string;
  status: string;
  createdAt: string;
}

interface KPICardProps {
  readonly title: string;
  readonly value: string | number;
  readonly icon: typeof Users;
}

function KPICard({ title, value, icon: Icon }: KPICardProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-border p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">{title}</p>
        <Icon className="w-4 h-4 text-nipost-green" />
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

const TYPE_COLORS: Record<string, string> = {
  PAYMENT_GATEWAY: 'bg-green-50 text-green-700',
  MDA: 'bg-blue-50 text-blue-700',
  SMS_PROVIDER: 'bg-purple-50 text-purple-700',
  LOGISTICS: 'bg-amber-50 text-amber-700',
  EXTERNAL: 'bg-gray-50 text-gray-700',
};

export function IntegrationDashboardPage(): JSX.Element {
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, partnerRes] = await Promise.all([
        request<DashboardKpis>(env.serviceAuditBaseUrl, '/v1/integration/dashboard'),
        request<{ partners: Partner[] }>(env.serviceAuditBaseUrl, '/v1/integration/partners'),
      ]);
      setKpis(dashRes);
      setPartners(partnerRes.partners);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integration partners"
        description="Active API integrations, health and SLA compliance."
        actions={
          <Link
            to="/integration/health"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg"
          >
            Health monitor
          </Link>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active partners"
          value={loading ? '…' : (kpis?.activePartners ?? 0)}
          icon={Users}
        />
        <KPICard
          title="Active webhooks"
          value={loading ? '…' : (kpis?.activeWebhooks ?? 0)}
          icon={Webhook}
        />
        <KPICard
          title="Webhooks today"
          value={loading ? '…' : (kpis?.webhooksDeliveredToday ?? 0)}
          icon={Timer}
        />
        <KPICard
          title="NIBSS status"
          value={loading ? '…' : (kpis?.nibssStatus ?? '—')}
          icon={Key}
        />
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Integration partners</h3>
          <Link
            to="/integration/partners"
            className="text-xs text-nipost-green hover:underline"
          >
            Manage
          </Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-muted text-sm">Loading partners…</div>
        ) : partners.length === 0 ? (
          <div className="p-8 text-center text-muted text-sm" data-testid="no-partners-notice">
            No partners onboarded yet.{' '}
            <Link to="/integration/partners" className="text-nipost-green hover:underline">
              Onboard one
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-gray-50" data-testid="partner-row">
                  <td className="p-3 font-medium text-gray-900">{p.name}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[p.partnerType] ?? 'bg-gray-100 text-gray-600'}`}>
                      {p.partnerType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{p.contactEmail}</td>
                  <td className="p-3 text-center"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
